import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useFieldArray,
  useFormContext,
  useFormState,
  useWatch,
} from 'react-hook-form';
import FormBlock from 'components/formComponents/Layout/FormBlock';
import TextBlock from 'components/formComponents/Base/TextBlock';
import { IsSmallScreenSize } from '../../../../../Functions/Responsiveness';
import { useImportTerminatedEmployeesMutation } from '../../../partyApplicationApi';
import styles from './ReportDismissedSection.module.scss';
import { CYPRESS_DATA } from './constants';
import EmptyState from './EmptyState';
import ImportReplaceDialog from './ImportReplaceDialog';
import TerminatedEmployeesFilters from './TerminatedEmployeesFilters';
import TerminatedEmployeesListMobile from './TerminatedEmployeesListMobile';
import TerminatedEmployeesTableBasic from './TerminatedEmployeesTableBasic';
import TerminatedEmployeesToolbar from './TerminatedEmployeesToolbar';
import { mapImportedEmployee } from './mappers/mapImportedEmployee';
import {
  EmployeeRowFilter,
  ImportError,
  ImportTerminatedEmployeesResponse,
  TerminatedEmployeeField,
  TerminatedEmployeeInput,
} from './types';
import {
  buildEmployeeRows,
  countRowsByStatus,
  EMPLOYEE_FIELDS,
  filterEmployeeRows,
} from './utils/buildEmployeeRows';
import { createEmptyEmployee } from './utils/createEmptyEmployee';
import { MAX_TERMINATED_EMPLOYEES } from './utils/validation';
import { useAppSelector } from '../../../../../hooks';
import {
  replaceTemplateTokens,
  resolveRegisterDismissedUiTexts,
  resolveRegisterDismissedWarningMessages,
} from './texts';

const FIELD_NAME = 'terminatedEmployees';
const IMPORT_SUCCESS_FLASH_MS = 5000;

const waitForNextAnimationFrame = async () =>
  new Promise<void>((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });

const resolveImportErrorMessage = (
  error: ImportError,
  uiTexts: ReturnType<typeof resolveRegisterDismissedUiTexts>
): string => {
  const template = (() => {
    switch (error.code) {
      case 'importNoFile':
      case 'importFileTooLarge':
      case 'importInvalidExtension':
      case 'importUnreadableFile':
      case 'importNoWorksheet':
      case 'importEmptyFile':
        return uiTexts.importUnreadableFile;
      case 'importNoPeople':
        return uiTexts.importNoPeople;
      case 'importMissingColumns':
        return uiTexts.importMissingColumns;
      case 'importMaxRowsWarning':
        return uiTexts.importMaxRowsWarning;
      case 'importUnknownEmploymentType':
        return uiTexts.importUnknownEmploymentType;
      case 'importInvalidDate':
        return uiTexts.importInvalidDate;
      case 'importUnexpectedError':
        return uiTexts.importErrorFallback;
      default:
        return error.message;
    }
  })();

  if (!error.tokens) {
    return template;
  }

  return replaceTemplateTokens(template, error.tokens);
};

const formatImportError = (
  error: ImportError,
  uiTexts: ReturnType<typeof resolveRegisterDismissedUiTexts>
): string => {
  const resolvedMessage = resolveImportErrorMessage(error, uiTexts);
  return [
    error.rowNumber ? `Rad ${error.rowNumber}` : null,
    error.column,
    resolvedMessage,
  ]
    .filter(Boolean)
    .join(' – ');
};

const getImportErrorMessages = (
  error: unknown,
  fallbackMessage: string,
  uiTexts: ReturnType<typeof resolveRegisterDismissedUiTexts>
): string[] => {
  const data = (
    error as { data?: ImportTerminatedEmployeesResponse } | undefined
  )?.data;
  if (data?.errors?.length) {
    return data.errors.map((importError) =>
      formatImportError(importError, uiTexts)
    );
  }
  return [fallbackMessage];
};

const ReportDismissedSection = () => {
  const { pageTexts, applicantRole } = useAppSelector(
    (state) => state.partyApplication
  );
  const texts = useMemo(() => pageTexts?.registerDismissedSection, [pageTexts]);
  const uiTexts = useMemo(
    () => resolveRegisterDismissedUiTexts(texts),
    [texts]
  );
  const warningMessages = useMemo(
    () => resolveRegisterDismissedWarningMessages(texts),
    [texts]
  );
  const { control, trigger, clearErrors } = useFormContext();
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: FIELD_NAME,
  });
  const { errors, isSubmitted, touchedFields, dirtyFields } = useFormState({
    control,
    name: FIELD_NAME,
  });

  const [filter, setFilter] = useState<EmployeeRowFilter>('all');
  const [search, setSearch] = useState('');
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [activatedFieldNames, setActivatedFieldNames] = useState<string[]>([]);
  const [pendingImportFile, setPendingImportFile] = useState<File | null>(null);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === 'undefined' ? 1280 : window.innerWidth
  );

  const [importTerminatedEmployees, { isLoading: isImporting }] =
    useImportTerminatedEmployeesMutation();

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!importSuccess) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setImportSuccess(null);
    }, IMPORT_SUCCESS_FLASH_MS);

    return () => window.clearTimeout(timeoutId);
  }, [importSuccess]);

  const values = useWatch({ control, name: FIELD_NAME }) as
    | TerminatedEmployeeInput[]
    | undefined;
  const hasMountedValidationTrigger = useRef(false);

  const hasAnyNestedInteraction = (
    value: unknown,
    isRootLevel = true
  ): boolean => {
    if (!value) {
      return false;
    }
    if (typeof value === 'boolean') {
      // React Hook Form can mark the whole array as dirty/touched on append/remove.
      // Treat only nested field booleans as actual user interaction.
      return isRootLevel ? false : value;
    }
    if (Array.isArray(value)) {
      return value.some((entry) => hasAnyNestedInteraction(entry, false));
    }
    if (typeof value === 'object') {
      return Object.values(value as Record<string, unknown>).some((entry) =>
        hasAnyNestedInteraction(entry, false)
      );
    }
    return false;
  };

  const hasRowInteraction =
    hasAnyNestedInteraction(
      (touchedFields as Record<string, unknown> | undefined)?.[FIELD_NAME]
    ) ||
    hasAnyNestedInteraction(
      (dirtyFields as Record<string, unknown> | undefined)?.[FIELD_NAME]
    );

  const liveValidationSignature = useMemo(
    () =>
      (values ?? [])
        .map((employee) =>
          [
            employee.firstName.trim(),
            employee.lastName.trim(),
            employee.employmentType.trim(),
            employee.startDate.trim(),
            employee.terminationDate.trim(),
            employee.endDate.trim(),
            employee.socialSecurityNumber.trim(),
            employee.email.trim().toLowerCase(),
            employee.phone.trim(),
          ].join('|')
        )
        .join('||'),
    [values]
  );

  useEffect(() => {
    if (!hasMountedValidationTrigger.current) {
      hasMountedValidationTrigger.current = true;
      return;
    }

    if (fields.length > 0 && (isSubmitted || hasRowInteraction)) {
      void trigger(FIELD_NAME, { shouldFocus: false });
    }
  }, [
    fields.length,
    hasRowInteraction,
    isSubmitted,
    liveValidationSignature,
    trigger,
  ]);

  const arrayErrors = errors?.[FIELD_NAME] as unknown;
  const rowErrors = Array.isArray(arrayErrors) ? arrayErrors : undefined;
  const rootMessage =
    !Array.isArray(arrayErrors) &&
    typeof (arrayErrors as { root?: { message?: unknown } })?.root?.message ===
      'string'
      ? ((arrayErrors as { root: { message: string } }).root.message as string)
      : null;
  const arrayErrorMessage =
    !Array.isArray(arrayErrors) &&
    typeof (arrayErrors as { message?: unknown })?.message === 'string'
      ? (arrayErrors as { message: string }).message
      : rootMessage;

  const hasFieldInteraction = useCallback(
    (rowIndex: number, fieldName: string): boolean =>
      activatedFieldNames.includes(`${FIELD_NAME}.${rowIndex}.${fieldName}`),
    [activatedFieldNames]
  );

  const visibleRowErrors = useMemo(() => {
    if (isSubmitted || !rowErrors) {
      return rowErrors;
    }

    return rowErrors.map((errorEntry, index) => {
      if (!errorEntry) {
        return undefined;
      }

      const shouldShowAllFields = isSubmitted || index < fields.length - 1;

      const filteredErrorEntry: Record<string, { message?: unknown }> = {};
      EMPLOYEE_FIELDS.forEach((fieldName) => {
        if (
          errorEntry[fieldName] &&
          (shouldShowAllFields || hasFieldInteraction(index, fieldName))
        ) {
          filteredErrorEntry[fieldName] = errorEntry[fieldName] as {
            message?: unknown;
          };
        }
      });

      return Object.keys(filteredErrorEntry).length > 0
        ? filteredErrorEntry
        : undefined;
    });
  }, [fields.length, hasFieldInteraction, isSubmitted, rowErrors]);

  const rows = useMemo(
    () =>
      buildEmployeeRows(
        fields,
        values,
        visibleRowErrors,
        applicantRole ?? undefined,
        undefined,
        warningMessages
      ),
    [fields, values, visibleRowErrors, applicantRole, warningMessages]
  );
  const counts = useMemo(() => countRowsByStatus(rows), [rows]);

  /**
   * A row being edited stays visible even when it stops matching the active filter,
   * so correcting the last error does not yank the input out from under the cursor.
   */
  const visibleRows = useMemo(() => {
    const matched = filterEmployeeRows(rows, filter, search);
    if (
      !editingFieldId ||
      matched.some((row) => row.fieldId === editingFieldId)
    ) {
      return matched;
    }
    const editingRow = rows.find((row) => row.fieldId === editingFieldId);
    return editingRow
      ? [...matched, editingRow].sort((a, b) => a.formIndex - b.formIndex)
      : matched;
  }, [rows, filter, search, editingFieldId]);

  const handleAdd = () => {
    const newIndex = fields.length;
    append(createEmptyEmployee());
    if (!isSubmitted) {
      clearErrors(`${FIELD_NAME}.${newIndex}`);
    }
    if (newIndex > 0) {
      void trigger(FIELD_NAME, { shouldFocus: false });
    }
    setFilter('all');
    setSearch('');
  };

  const handleRemove = (fieldId: string) => {
    const index = fields.findIndex((field) => field.id === fieldId);
    if (index < 0) {
      return;
    }
    remove(index);
  };

  const handleEditingChange = (
    fieldId: string,
    fieldName: string,
    isEditing: boolean
  ) => {
    if (isEditing) {
      setActivatedFieldNames((current) =>
        current.includes(fieldName) ? current : [...current, fieldName]
      );
    }

    setEditingFieldId((current) => {
      if (isEditing) {
        return fieldId;
      }
      return current === fieldId ? null : current;
    });
  };

  const importedFieldPaths = (
    rowCount: number
  ): `${typeof FIELD_NAME}.${number}.${TerminatedEmployeeField}`[] => {
    const fieldPaths: `${typeof FIELD_NAME}.${number}.${TerminatedEmployeeField}`[] =
      [];
    for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
      EMPLOYEE_FIELDS.forEach((fieldName) => {
        fieldPaths.push(
          `${FIELD_NAME}.${rowIndex}.${fieldName}` as `${typeof FIELD_NAME}.${number}.${TerminatedEmployeeField}`
        );
      });
    }

    return fieldPaths;
  };

  const runImport = async (file: File) => {
    setImportErrors([]);
    setImportSuccess(null);
    const body = new FormData();
    body.append('file', file);

    try {
      const result = await importTerminatedEmployees(body).unwrap();
      const importedEmployees = result.employees.map(mapImportedEmployee);
      replace(importedEmployees);
      setFilter('all');
      setSearch('');
      setImportSuccess(uiTexts.importSuccess);
      setImportErrors(
        result.errors.map((importError) =>
          formatImportError(importError, uiTexts)
        )
      );

      // Wait for the replaced field array to mount/register before validating.
      await waitForNextAnimationFrame();
      const importedPaths = importedFieldPaths(importedEmployees.length);
      setActivatedFieldNames(importedPaths);
      await trigger([FIELD_NAME, ...importedPaths], { shouldFocus: false });
    } catch (error) {
      setImportErrors(
        getImportErrorMessages(error, uiTexts.importErrorFallback, uiTexts)
      );
    }
  };

  const handleFileSelected = (file: File) => {
    setImportErrors([]);
    setImportSuccess(null);
    if (fields.length > 0) {
      setPendingImportFile(file);
      return;
    }
    void runImport(file);
  };

  const handleConfirmReplace = () => {
    const file = pendingImportFile;
    setPendingImportFile(null);
    if (file) {
      void runImport(file);
    }
  };

  const isSmallScreen = IsSmallScreenSize(viewportWidth);
  const showFilterEmptyInfo =
    counts.total > 0 &&
    ((filter === 'errors' && counts.errors === 0) ||
      (filter === 'warnings' && counts.warnings === 0));
  const filterEmptyInfoText =
    filter === 'warnings'
      ? uiTexts.filterWarningsEmptyInfo
      : uiTexts.filterErrorsEmptyInfo;

  return (
    <FormBlock
      title={uiTexts.title}
      dataCy={CYPRESS_DATA.section}
      isLoading={isImporting}
    >
      <div data-error-for={FIELD_NAME} />
      <div
        className={styles.preamble}
        dangerouslySetInnerHTML={{
          __html: uiTexts.preamble,
        }}
      />

      {importSuccess && (
        <TextBlock
          type="info"
          text={importSuccess}
          dataCy={CYPRESS_DATA.importSuccess}
        />
      )}

      {importErrors.length > 0 && (
        <div
          className={styles.importErrors}
          data-cy={CYPRESS_DATA.importErrors}
        >
          {importErrors.map((message) => (
            <TextBlock key={message} type="warning" text={message} />
          ))}
        </div>
      )}

      {fields.length === 0 ? (
        <EmptyState
          isImporting={isImporting}
          onAdd={handleAdd}
          onFileSelected={handleFileSelected}
          registerDismissedAddBtnText={uiTexts.addButton}
          importFileBtnText={uiTexts.importButton}
          downloadTemplateBtnText={uiTexts.downloadTemplateButton}
          downloadCopyBtnText={uiTexts.downloadCopyButton}
          templateFileUrl={uiTexts.templateFileUrl}
        />
      ) : (
        <>
          <TerminatedEmployeesToolbar
            count={fields.length}
            maxCount={MAX_TERMINATED_EMPLOYEES}
            isImporting={isImporting}
            employees={rows.map((row) => row.employee)}
            registerDismissedAddBtnText={uiTexts.addButton}
            importFileBtnText={uiTexts.importButton}
            downloadTemplateBtnText={uiTexts.downloadTemplateButton}
            downloadCopyBtnText={uiTexts.downloadCopyButton}
            templateFileUrl={uiTexts.templateFileUrl}
            onAdd={handleAdd}
            onFileSelected={handleFileSelected}
          />

          <TerminatedEmployeesFilters
            filter={filter}
            search={search}
            allLabel={replaceTemplateTokens(uiTexts.filterAll, {
              count: counts.total,
            })}
            errorsLabel={replaceTemplateTokens(uiTexts.filterErrors, {
              count: counts.errors,
            })}
            warningsLabel={replaceTemplateTokens(uiTexts.filterWarnings, {
              count: counts.warnings,
            })}
            searchPlaceholder={uiTexts.searchPlaceholder}
            onFilterChange={setFilter}
            onSearchChange={setSearch}
          />

          {showFilterEmptyInfo && isSmallScreen && (
            <TextBlock
              type="info"
              text={filterEmptyInfoText}
              dataCy={CYPRESS_DATA.allCorrected}
            />
          )}

          {isSmallScreen ? (
            <TerminatedEmployeesListMobile
              rows={visibleRows}
              texts={uiTexts}
              onRemove={handleRemove}
            />
          ) : (
            <TerminatedEmployeesTableBasic
              rows={visibleRows}
              texts={uiTexts}
              emptyInfoText={showFilterEmptyInfo ? filterEmptyInfoText : null}
              onRemove={handleRemove}
              onEditingChange={handleEditingChange}
            />
          )}
        </>
      )}

      {arrayErrorMessage && (
        <div data-error-for={FIELD_NAME} tabIndex={-1}>
          <TextBlock type="error" text={arrayErrorMessage} />
        </div>
      )}

      <ImportReplaceDialog
        isOpen={pendingImportFile !== null}
        title={uiTexts.replaceDialogTitle}
        description={replaceTemplateTokens(uiTexts.replaceDialogDescription, {
          count: fields.length,
        })}
        acceptLabel={uiTexts.replaceDialogAccept}
        cancelLabel={uiTexts.replaceDialogCancel}
        onAccept={handleConfirmReplace}
        onCancel={() => setPendingImportFile(null)}
      />
    </FormBlock>
  );
};

export default ReportDismissedSection;
